import { useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import { useToastStore } from '@pkg/components/Toast/useToastStore';
import { useQueryInvalidation } from '@pkg/hooks/reactQuery';
import { useAppDispatch } from '@pkg/hooks/redux';
import { useUpdateUser } from '@pkg/hooks/user/useUpdateUser';

import { type EmojiId } from '@app/client';
import { USER_YATS_QUERY_CACHE } from '@app/pages/ProfilePage/sections/YatsSection/hooks/useGetUserYats';
import { setPrimaryYat } from '@app/store/actions/user';
import { getYatPageDetails } from '@app/store/actions/yat-page';

export function useUpdatePrimaryYat(eid: EmojiId) {
    const { yatParam } = useParams();
    const CACHES = [USER_YATS_QUERY_CACHE, eid];
    const { t } = useTranslation('primary_yat');

    const { mutateAsync: updateUser } = useUpdateUser();
    const invalidateQueries = useQueryInvalidation(CACHES);
    const { addToast } = useToastStore();
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();

    const handleUrl = useCallback(() => {
        if (yatParam?.length && yatParam !== eid) {
            const path = pathname.replace(`/${encodeURIComponent(yatParam)}`, '');
            const newPath = `/${eid}${path}`;

            window.history.pushState({}, '', newPath);
        }
    }, [eid, pathname, yatParam]);

    return useCallback(async () => {
        updateUser({ value: { eid, confirm_eid: true }, dispatch }).then((r) => {
            if (r.ok && r.value) {
                dispatch(setPrimaryYat(eid));
                dispatch(getYatPageDetails(eid)).then((r) => {
                    if (r?.ok && r?.value?.version) {
                        handleUrl();
                    }
                });
                invalidateQueries();
                addToast({
                    emoji: '🖖',
                    message: t('yat_successfully_updated'),
                });
            }
        });
    }, [addToast, dispatch, eid, handleUrl, invalidateQueries, t, updateUser]);
}
